import BaseApiService from './BaseApiService'

export type Register = 'risk' | 'vendor' | 'training' | 'decision'

export interface GovernanceRow {
  governanceRecordId: string
  register: Register
  recordKey: string
  title: string
  description: string | null
  owner: string | null
  status: string
  likelihood: number | null
  impact: number | null
  score: number | null
  treatment: string | null
  tier: number | null
  assurance: string | null
  reviewIntervalDays: number
  lastReviewedAt: string | null
  reviewNotes: string | null
  evidenceFileName: string | null
  nextReviewAt: string
  reviewDue: boolean
}

export interface GovernanceSave {
  governanceRecordId?: string | null
  register: Register
  recordKey: string
  title: string
  description?: string | null
  owner?: string | null
  status: string
  likelihood?: number | null
  impact?: number | null
  treatment?: string | null
  tier?: number | null
  assurance?: string | null
  reviewIntervalDays: number
  completedAt?: string | null
}

export interface PolicyRow {
  name: string
  title: string
  effective: string | null
  sha256: string
  acknowledgedByMeAt: string | null
  acknowledgements: { userId: string; email: string | null; acknowledgedAt: string }[]
  previousVersionAcknowledged: boolean
}

/** Statuses each register accepts (mirrors GovernanceController.Statuses). */
export const STATUSES: Record<Register, string[]> = {
  risk: ['open', 'mitigated', 'accepted', 'closed'],
  vendor: ['active', 'retired'],
  training: ['complete'],
  decision: ['approved', 'superseded'],
}

class GovernanceApiService extends BaseApiService {
  list(register?: Register) {
    return this.post<GovernanceRow[]>('/v1/admin/governance/list', { register: register ?? null })
  }

  save(record: GovernanceSave) {
    return this.post<{ governanceRecordId: string }>('/v1/admin/governance/save', record)
  }

  review(governanceRecordId: string, notes: string, evidence?: File | null) {
    const form = new FormData()
    form.append('governanceRecordId', governanceRecordId)
    form.append('notes', notes)
    if (evidence) form.append('evidence', evidence)
    return this.postForm<{ success: boolean }>('/v1/admin/governance/review', form)
  }

  evidence(governanceRecordId: string) {
    return this.request<Blob>('POST', '/v1/admin/governance/evidence', { governanceRecordId }, true)
  }

  policies() {
    return this.post<PolicyRow[]>('/v1/admin/governance/policies')
  }

  acknowledge(policies: { name: string; sha256: string }[]) {
    return this.post<{ recorded: number }>('/v1/admin/governance/policies/acknowledge', { policies })
  }
}

export const governanceApiService = new GovernanceApiService()
